import { FormEvent, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { authActions, authSelectors } from 'state/auth';
import { useAppDispatch, useAppSelector } from 'hooks/useRedux';
import Alert from 'react-bootstrap/Alert';
import { VALUE_TOO_LONG, INVALID_EMAIL, FIELD_MISSING } from 'constants/validationMessages';
import './ForgotPassword.scss';
import ErrorBadge from 'components/ErrorBadge/ErrorBadge';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { emailRegex } from 'constants/validationRules';
import { Form } from 'react-bootstrap';
import NameHeader from 'components/nameHeader/NameHeader';
import ColoredLine from 'components/ColoredLine/ColoredLine';
import InfoBar from 'components/InfoBar/InfoBar';
import { ConfigurableHyperLinkSelector } from 'state/hyperlink/hyperlink.selectors';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { CheckSquareIcon } from 'assets/icons/CheckSquareIcon';
import { ExclamationSquareIcon } from 'assets/icons/ExclamationSquareIcon';
import WorkflowNavigationButton from 'components/Buttons/WorkflowNavigationButton/WorkflowNavigationButton';
interface DefaultValues {
  email: string;
}

const ForgotPassword = () => {
  const hyperlinks = useAppSelector(ConfigurableHyperLinkSelector);

  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const [isLoader, setIsLoader] = useState(false);
  const [showMessage, setShowMessage] = useState(false);

  const defaultValues = { email: '' };

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({ defaultValues });

  const handleReturnToLoginClick = (event: FormEvent) => {
    event.preventDefault();
    navigate('/');
    dispatch(authActions.setForgotPasswordError(''));
  };

  const error = useAppSelector(authSelectors.forgotPasswordErrorSelector);

  const onSubmit = async (data: DefaultValues) => {
    setIsLoader(true);
    await dispatch(authActions.forgotPassword({ email: data.email }));

    if (!error) {
      setShowMessage(true);
    }
    setIsLoader(false);
  };

  return (
    <div className="w-100">
      <NameHeader
        serviceType={false}
        disableLogoOnClick={true}
      />
      {showMessage && !error && (
        <Alert
          variant={'success'}
          className="text-center small mb-0"
        >
          <div className="d-inline-flex align-items-center">
            <div className="me-1 d-flex align-items-center">
              <CheckSquareIcon />
            </div>{' '}
            Email was sent
          </div>
        </Alert>
      )}
      {error && (
        <Alert
          variant={'danger'}
          className="text-center small mb-0"
        >
          <div className="d-inline-flex align-items-center">
            <div className="me-1 d-flex align-items-center">
              <ExclamationSquareIcon />
            </div>{' '}
            {error}
          </div>
        </Alert>
      )}
      <div className="w-100 forgot-password-wrapper">
        <div className="bg-light-purple">
          <ColoredLine />
          <div className="p-4 border-bottom border-4 border-white">
            <h2 style={{ fontWeight: 700 }}>Forgot Your Password? </h2>
            <p>
              Enter your email address below to receive an email with instructions on resetting your
              password.
            </p>
            <Form onSubmit={handleSubmit(onSubmit)}>
              <Form.Group className="mb-3">
                <Form.Label className="text-black">
                  Email<span className="text-danger"> *</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  id="email-input-forgot-password"
                  data-testid="email-input-login"
                  disabled={showMessage && !error}
                  className="form-control rounded-pill"
                  {...register('email', {
                    required: {
                      value: true,
                      message: FIELD_MISSING,
                    },
                    pattern: {
                      value: emailRegex,
                      message: INVALID_EMAIL,
                    },
                    maxLength: {
                      value: 100,
                      message: VALUE_TOO_LONG,
                    },
                  })}
                />
                <ErrorBadge
                  errors={errors}
                  formKey="email"
                />
              </Form.Group>
              <WorkflowNavigationButton
                id="submit-button-forgot-password"
                text="Submit"
                variant="danger"
                className="mt-1"
                disabled={showMessage && !error}
              >
                {isLoader && (
                  <FontAwesomeIcon
                    icon={faSpinner}
                    color="white"
                    spinPulse={true}
                  />
                )}
              </WorkflowNavigationButton>
              <div className="col-12 mt-3">
                <a
                  id="return-to-login-anchor-forgot-password"
                  href="REMS - Login.html"
                  onClick={handleReturnToLoginClick}
                  className="text-purple text-decoration-none "
                >
                  Return to Login
                </a>
              </div>
            </Form>
          </div>
          <InfoBar center={true}>
            Having trouble logging in?{' '}
            <a
              id="chat-with-us-anchor-login"
              className="text-purple text-decoration-none"
              href={hyperlinks['support_chat_url']}
              target="_blank"
            >
              Chat with us
            </a>{' '}
            or contact support at (855) 863-1355.
          </InfoBar>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
